// One bounded acquisition, no coordinate history and no background tracking.
export const LOCATION_MESSAGES = {
  LOCATION_PERMISSION_DENIED: "Location access is blocked. Allow Location for this website in browser Site Settings and for the browser in phone settings, then tap Check My Location / retry. Phone Location ON alone does not grant website access.",
  SECURE_CONTEXT_REQUIRED: "Location requires HTTPS. Open https://sms.dfa.com.ng in your normal browser.",
  GEOLOCATION_UNSUPPORTED: "This browser does not provide location. Open https://sms.dfa.com.ng in Chrome or your normal browser.",
  LOCATION_TIMEOUT: "A precise location could not be obtained in time. Check Location settings and try again.",
  POSITION_UNAVAILABLE: "The browser could not determine your position. Check phone and browser Location settings, enable precise location if available, move near a window or outdoors, then retry.",
  LOCATION_STALE: "The browser returned an old location. Check Location settings and retry for a fresh position.",
  LOCATION_CANCELLED: "Location check cancelled. Retry when ready.",
  UNKNOWN_GEOLOCATION_ERROR: "Location could not be obtained. Retry in your normal browser.",
};
export const inAppBrowserHint = (ua = "") => /(; wv\)|\bFBAN\b|\bFBAV\b|Instagram|WhatsApp)/i.test(ua);

export function getBrowserLocation({timeout = 30000, accuracyThreshold = 50, signal,
  navigatorApi = globalThis.navigator, windowApi = globalThis.window, onState = () => {}} = {}) {
  return new Promise((resolve, reject) => {
    const started = Date.now(), geo = navigatorApi?.geolocation;
    let watchId, timer, settled = false, best, stale = false, lastCode, sampleCount = 0;
    const diagnostics = {permission_state: "unknown", in_app_browser_hint: inAppBrowserHint(navigatorApi?.userAgent)};
    const finish = (code) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (watchId != null) geo.clearWatch(watchId);
      signal?.removeEventListener("abort", cancel);
      windowApi?.removeEventListener?.("pagehide", cancel);
      Object.assign(diagnostics, {acquisition_duration_ms: Date.now() - started, sample_count: sampleCount,
        location_age_ms: best ? Date.now() - Date.parse(best.client_timestamp) : null,
        gps_accuracy: best?.accuracy_metres});
      if (!code) return resolve({...best, diagnostics});
      let message = LOCATION_MESSAGES[code];
      if (code === "POOR_GPS_ACCURACY") message = `Your location was found, but it is not precise enough. Current accuracy: ${Math.round(best.accuracy_metres)} m. Required: ${accuracyThreshold} m. Move near a window or outdoors, enable precise/high-accuracy location, then retry.`;
      if (diagnostics.in_app_browser_hint) message += " This may be an in-app browser. Open https://sms.dfa.com.ng in Chrome or your normal browser.";
      reject(Object.assign(new Error(message), {attendanceCode: code, diagnostics}));
    };
    const cancel = () => finish("LOCATION_CANCELLED");
    const expired = () => finish(best && Date.now() - Date.parse(best.client_timestamp) <= 15000 ? "POOR_GPS_ACCURACY" : best || stale ? "LOCATION_STALE" : lastCode || "LOCATION_TIMEOUT");
    if (signal?.aborted) return cancel();
    if (windowApi?.isSecureContext === false) return finish("SECURE_CONTEXT_REQUIRED");
    if (!geo || typeof geo.watchPosition !== "function") return finish("GEOLOCATION_UNSUPPORTED");
    signal?.addEventListener("abort", cancel, {once: true});
    windowApi?.addEventListener?.("pagehide", cancel, {once: true});
    // Covers permission UI too; a pending Permissions API promise cannot hang acquisition.
    timer = setTimeout(expired, timeout);
    try {
      const query = navigatorApi.permissions?.query?.({name: "geolocation"});
      Promise.resolve(query).then(permission => {
        if (!settled && permission) {
          diagnostics.permission_state = permission.state;
          onState(permission.state === "prompt" ? "LOCATION_PERMISSION_PROMPT" : "LOCATING");
        }
      }).catch(() => {});
    } catch (_) { /* Optional API; geolocation remains authoritative on every retry. */ }
    try {
      watchId = geo.watchPosition(({coords, timestamp}) => {
        if (settled) return;
        sampleCount++;
        const age = Date.now() - timestamp;
        if (!Number.isFinite(timestamp) || timestamp < started || age > 15000 || age < -1000) {stale = true; return;}
        if (![coords.latitude, coords.longitude, coords.accuracy].every(Number.isFinite) || coords.accuracy <= 0 || Math.abs(coords.latitude) > 90 || Math.abs(coords.longitude) > 180) {lastCode = "POSITION_UNAVAILABLE"; return;}
        if (!best || Date.now() - Date.parse(best.client_timestamp) > 15000 || coords.accuracy < best.accuracy_metres)
          best = {latitude: coords.latitude, longitude: coords.longitude, accuracy_metres: coords.accuracy, client_timestamp: new Date(timestamp).toISOString()};
        if (best.accuracy_metres <= Math.min(50, accuracyThreshold)) finish();
      }, cause => {
        if (settled) return;
        diagnostics.geolocation_error_code = cause?.code;
        lastCode = ({1: "LOCATION_PERMISSION_DENIED", 2: "POSITION_UNAVAILABLE", 3: "LOCATION_TIMEOUT"})[cause?.code] || "UNKNOWN_GEOLOCATION_ERROR";
        if (cause?.code === 1) {diagnostics.permission_state = "denied"; finish(lastCode);}
        // A transient unavailable/timeout may recover before the overall deadline.
      }, {enableHighAccuracy: true, timeout, maximumAge: 0});
      if (settled && watchId != null) geo.clearWatch(watchId);
    } catch (_) {finish("UNKNOWN_GEOLOCATION_ERROR");}
  });
}
